import React, { FC, useId, useState } from "react";
import { ArrowLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { dailyRate, formatPrice, getEnabledPeriodPrices } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { isNotBookable, isPriceOnRequest } from "utils/carPrice";
import CarDetailsDialog from "designs/DesignB/components/CarDetails/CarDetailsDialog";
import CarSheet from "designs/DesignB/components/CarDetails/CarSheet";
import { galleryPhotos } from "designs/DesignB/components/CarDetails/carDetails";
import CarPhoto from "../BookingSteps/CarPhoto";
import { useBackOrHome } from "shared/BackRouteButton/useBackOrHome";
import { useIsSheet } from "./useIsSheet";

/**
 * "← Back" (1NOW-11228; 1NOW-11176's "‹ All cars", which went to /vehicles from anywhere but a car list).
 * Back to the page the renter came from on this site, home from a link. 44px tall, so a thumb finds it.
 */
export const BackToCars: FC<{ className?: string }> = ({ className = "" }) => {
  const goBack = useBackOrHome();

  return (
    <button
      type="button"
      onClick={goBack}
      className={`inline-flex min-h-[44px] items-center gap-1.5 pr-2 text-sm font-medium hover:underline ${className}`}
    >
      <ArrowLeftIcon className="h-4 w-4" />
      Back
    </button>
  );
};

export interface CarTopBProps {
  car: any;
}

/**
 * The top of the Design B car page. 1NOW-11176 drew the car here: the photo grid, the name, the price and the
 * key specs. 1NOW-11219 moved all of the car's details into the fleet card's panel, so the car page is about
 * booking: "← Back", then one line that confirms the car (a small photo, the name, the price) and a
 * "Car details" button for a renter who came straight here and never saw the panel. It opens the panel's own
 * content: a dialog from 768px, the full-screen sheet below.
 */
const CarTopB: FC<CarTopBProps> = ({ car }) => {
  const { ourCompany } = useData();
  const sheet = useIsSheet();
  const titleId = useId();
  const [details, setDetails] = useState<"open" | "closing" | null>(null);

  const notBookable = isNotBookable(car);
  const rate = dailyRate(car) ?? 0;
  const primary = notBookable ? null : getEnabledPeriodPrices(rate, ourCompany, car?.period_prices)?.[0];

  return (
    <section data-testid="car-top-b" className="cpb md:mt-2">
      {/* Its 44px take the 12px margins the 20px link had, so the line sits where it did. */}
      <BackToCars className="text-text-color" />

      {/* The photo, then the name over Car details and the price. Below 768px the price shares Car details' line,
          so a long name has the width the price would take (it wrapped 13 lines at 320px beside both). */}
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 border-b border-text-color/10 pb-5 md:grid-cols-[auto_minmax(0,1fr)_auto] md:gap-x-4">
        <CarPhoto
          photo={galleryPhotos(car)[0]}
          className="row-span-2 self-start h-14 w-20 rounded-xl bg-text-color/[0.06] md:h-16 md:w-24"
        />
        <h1 className="col-start-2 break-words text-lg font-bold leading-tight tracking-tight text-text-color md:text-2xl">
          {car?.name}
        </h1>
        <div className="col-start-2 flex flex-wrap items-center justify-between gap-x-3 md:contents">
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setDetails("open")}
            className="mt-0.5 inline-flex min-h-[32px] items-center gap-1 whitespace-nowrap text-sm font-semibold text-text-color underline underline-offset-4 hover:no-underline md:col-start-2 md:row-start-2 md:justify-self-start"
          >
            Car details
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <div
            className="whitespace-nowrap text-right md:col-start-3 md:row-span-2 md:row-start-1"
            data-testid="car-top-price"
          >
            {notBookable ? (
              <span className="text-sm font-semibold text-text-color">
                {isPriceOnRequest(car) ? "Price on request" : "Price coming soon"}
              </span>
            ) : (
              <>
                <span className="text-xl font-bold leading-none text-text-color md:text-2xl">
                  {primary?.formatted ?? `$${formatPrice(rate)}`}
                </span>
                <span className="ml-1 text-sm text-text-color/60">{primary?.label ?? "/ day"}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <CarDetailsDialog car={car} titleId={titleId} open={details === "open" && !sheet} onClose={() => setDetails(null)} />
      {sheet && details && (
        <CarSheet
          car={car}
          titleId={`${titleId}-sheet`}
          closing={details === "closing"}
          ownHistory
          onClose={() => setDetails("closing")}
          onGone={() => setDetails(null)}
        />
      )}
    </section>
  );
};

export default CarTopB;
